/**
 * The dock's Strip map tab (strip-map spec §3): the controls that shape the
 * picture, and the picture itself.
 *
 * A component of its own rather than another block inside `StationVolumeDock`
 * for two reasons. The dock's own hunk stays eight lines — it is already
 * 2 400 lines long and every pane it grows inline makes the next one harder
 * to add. And the tolerance/span controls are STATEFUL in a way the dock is
 * not: they read and write two per-layer `localStorage` atoms, and the strip
 * is the only surface that has an opinion about either.
 *
 * The offsets fetch here is the REAL one (`enabled: true`), where the dock's
 * own call is cache-only (`enabled: false`). Same react-query key, so the two
 * share one decode of `station_offsets.f32` — the tab pays for the bytes when
 * the reader opens it, and the Export menu's PNG gets them for free
 * afterwards.
 *
 * A LAYER_STACK run takes the OTHER branch throughout (spec §3.6, ruling
 * A-3). It writes no `station_offsets.f32` and so has no `dz` plane at all;
 * its strip is the selected layer's THICKNESS — its deviation from design
 * where the run knows one, the raw thickness with the band off where it does
 * not — computed here from the same `station_sections.f32` the Section pane
 * draws. Everything downstream of `matrix` is shared: one canvas, one set of
 * controls, one set of atoms, so a stack strip and a pair strip cannot drift
 * into two different pictures.
 */
import { stripSpanMAtom, stripToleranceMAtom } from '@/atoms/stationVolumes.atom';
import { DOCK_CONTROL } from '@/components/dock/dockStyles';
import { LoadingBadge } from '@/components/ui/LoadingBadge';
import { Button } from '@/components/ui/button';
import { NumericField } from '@/components/ui/numeric-field';
import { Slider } from '@/components/ui/slider';
import { useStationOffsets, useStationSections } from '@/hooks/useStationVolumes';
import { downloadFile } from '@/lib/download';
import type { StackDockData } from '@/lib/layerStack/dockModel';
import {
  stripPngBlob,
  stripPngFilename,
  stripPngInputFor,
  type StripPngSource,
} from '@/lib/stationVolumes/stripPng';
import {
  SPAN_SLIDER_STEPS,
  p98AbsFinite,
  spanFromSliderPosition,
  spanSliderPosition,
  stackThicknessMatrix,
} from '@/lib/stationVolumes/stripModel';
import type {
  StationVolumesAnalysisKind,
  StationVolumesJson,
  StationVolumesLayerMeta,
} from '@/lib/stationVolumes/types';
import { cn } from '@/lib/utils';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { StripMapCanvas } from './StripMapCanvas';

/**
 * The run's own tolerances, whichever summary block carries them — a
 * deviation run's `deviationStats.corridor.expectation`
 * (`StationCorridorExpectation`) or a surface-difference run's
 * `stats.expectation` (`ExpectationMeta`). Structural on purpose: the strip
 * needs exactly these two numbers and nothing else either block holds.
 */
export interface StripRunExpectation {
  underToleranceM: number | null;
  overToleranceM: number | null;
}

/** The `atomFamily` key the settings hook uses when it is called with no
 *  layer — see `useStripSettings`. */
const NO_LAYER = '__none__';

/** The strip's default neutral band when nothing else decides it: ±20 mm
 *  (ruling A-2), the tolerance a road surface is normally set out to. */
export const DEFAULT_STRIP_TOLERANCE_M = 0.02;

/**
 * Compose the strip PNG and hand it to the browser — for BOTH callers, this
 * tab's own button and the dock's Export menu.
 *
 * The try/catch is the reason this is a function and not two call sites:
 * `stripPngBlob` REJECTS when the canvas refuses to encode, and either caller
 * fires it from a click handler that cannot await. Unguarded, the item would
 * look like it worked and no file would arrive.
 */
export async function saveStripPng(src: StripPngSource): Promise<void> {
  try {
    const blob = await stripPngBlob(stripPngInputFor(src));
    downloadFile(blob, stripPngFilename(src.json.alignment.name), 'image/png');
  } catch (e) {
    toast.error(e instanceof Error ? e.message : String(e));
  }
}

/**
 * The two ramp settings, resolved once for BOTH callers — this tab's controls
 * and the dock's Export menu, which composes the same PNG without the tab
 * ever having been opened. Two copies of this precedence would be two
 * pictures of one run.
 *
 * Precedence (spec §3.2): the RUN's own tolerance wins outright, because a
 * run that was measured against a tolerance is judged by that tolerance and
 * nothing the reader types can change what was measured. Only a run with no
 * expectation of its own leaves the choice open, and then the per-layer
 * stored value wins over the ±20 mm default.
 */
/** Floor for the span the colour ramp divides by. Only guards a zero or a
 *  half-typed value reaching the LUT; it is never written back to what the
 *  user typed, so a decimal can be entered one keystroke at a time. */
const MIN_RENDER_SPAN_M = 0.001;

export function useStripSettings(
  layerId: string | null,
  expectation: StripRunExpectation | null,
  values: ArrayLike<number> | null,
) {
  // `null` is a real caller: the dock runs this hook above its own early
  // return, so it asks with no layer open. Key that on a sentinel rather than
  // on `''`, which would persist to a storage key ending in a bare dot and
  // collect settings nobody can ever read back.
  const key = layerId || NO_LAYER;
  const [storedTol, setStoredTol] = useAtom(stripToleranceMAtom(key));
  const [storedSpan, setStoredSpan] = useAtom(stripSpanMAtom(key));
  // A-1: one band, so the wider of the two one-sided tolerances — a cell
  // inside the run's tolerance on either side must not be inked.
  const runTolM =
    expectation && (expectation.underToleranceM != null || expectation.overToleranceM != null)
      ? Math.max(expectation.underToleranceM ?? 0, expectation.overToleranceM ?? 0)
      : null;
  const toleranceM = runTolM ?? storedTol ?? DEFAULT_STRIP_TOLERANCE_M;
  // The same p98 symmetric-span RULE the 3D corridor bands use
  // (`aggregate.ts::symmetricDomain`), applied to a different quantity: the
  // bands take p98 of |net m³/m| per BIN, the strip takes p98 of |Δz| per
  // CELL, in metres. Same method, different unit — the two scales are never
  // the same number, and matching them would be matching m to m³/m.
  const autoSpanM = useMemo(() => (values && values.length ? p98AbsFinite(values) : 0.05), [values]);
  // Two spans, deliberately. `spanM` is what the user typed and what the input
  // shows; `renderSpanM` is what the colour ramp divides by. Clamping inside
  // the input's onChange instead — which is what this did — made a decimal
  // impossible to type: entering "0.5" pushes "0" on the first keystroke, the
  // clamp rewrote it to the minimum, the changed value prop resynced the field
  // and the half-typed number was gone.
  const spanM = storedSpan ?? autoSpanM;
  const renderSpanM = Math.max(MIN_RENDER_SPAN_M, spanM);
  return {
    runTolM,
    toleranceM,
    spanM,
    renderSpanM,
    autoSpanM,
    storedTol,
    setStoredTol,
    storedSpan,
    setStoredSpan,
  };
}

export interface StripMapTabProps {
  json: StationVolumesJson;
  meta: StationVolumesLayerMeta | null;
  layerId: string;
  batchId?: string;
  kind: StationVolumesAnalysisKind;
  /** The run's own tolerances, when it was measured against any. Ignored for a
   *  stack, which carries none run-wide — see `stack.levelTolerances`. */
  expectation: StripRunExpectation | null;
  /** The multi-layer stack's selected layer (ruling A-3). Present ONLY for a
   *  LAYER_STACK run, and its presence is what switches this component from
   *  the `dz` plane to the section planes. */
  stack?: StackDockData | null;
}

export const StripMapTab = ({ json, meta, layerId, batchId, kind, expectation, stack }: StripMapTabProps) => {
  const { t } = useTranslation('project');
  // Mutually exclusive sources, and each disables the other's fetch: a stack
  // has no `station_offsets.f32` to ask for, and a pair run's section planes
  // are the Section tab's business, not the strip's. One flag rather than two
  // independent `enabled`s, so they cannot both be on.
  const isStack = !!stack;
  // `!isStack`, not the dock's `false`: this component only mounts when the
  // tab is open, so opening it is exactly when the bytes are worth fetching.
  const offsetsQ = useStationOffsets(meta, batchId, !isStack);
  const sectionsQ = useStationSections(meta, batchId, isStack);
  const offsets = offsetsQ.data;
  const sections = sectionsQ.data;

  // ------------------------------------------------------------- its own box
  //
  // Measured, not inherited: the strip is the one pane whose HEIGHT matters
  // (the offset axis is drawn down it), and the dock only ever measures its
  // width.
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ width: 800, height: 220 });
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      setBox((prev) => ({
        width: typeof r.width === 'number' && r.width > 0 ? r.width : prev.width,
        height: typeof r.height === 'number' && r.height > 0 ? r.height : prev.height,
      }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ------------------------------------------------------------- the picture
  const offsetsMatrix = useMemo(
    () => (offsets ? { values: offsets.dz, nStations: offsets.nStations, nOffsets: offsets.nOffsets } : null),
    [offsets],
  );
  const stackStrip = useMemo(
    () => (stack && sections ? stackThicknessMatrix(sections, stack.planePair, json.bins) : null),
    [stack, sections, json.bins],
  );
  const matrix = isStack ? (stackStrip?.matrix ?? null) : offsetsMatrix;
  /** RAW thickness: nothing to be a tolerance band AROUND (ruling A-3). */
  const bandOff = isStack && !stackStrip?.designed;

  /**
   * A stack's tolerances are per LEVEL, not per run, and `stack_analysis.py`
   * judges layer `i` against level `i`'s pair — the top of the two. The
   * selected layer's index is where its own top plane sits in the file's plane
   * list, which is exactly how `stackPlanePair` chose the pair in the first
   * place. Without this the strip would fall back to the ±20 mm default and
   * draw a band the run was never measured against.
   *
   * `null` while the values are raw: quoting "±10 mm · run tolerance" beside a
   * thickness that is not a deviation from anything is a false statement.
   */
  const stackExpectation = useMemo((): StripRunExpectation | null => {
    if (!stack?.planePair || !stackStrip?.designed) return null;
    const names = sections?.planeNames ?? meta?.sections?.planes ?? null;
    const level = names ? names.indexOf(stack.planePair[0]) : -1;
    const tol = level >= 0 ? (stack.levelTolerances?.[level] ?? null) : null;
    return tol ? { underToleranceM: tol.underToleranceM, overToleranceM: tol.overToleranceM } : null;
  }, [stack, stackStrip, sections, meta]);

  const { runTolM, toleranceM, spanM, renderSpanM, autoSpanM, setStoredTol, storedSpan, setStoredSpan } = useStripSettings(
    layerId,
    isStack ? stackExpectation : expectation,
    matrix?.values ?? null,
  );
  const effectiveToleranceM = bandOff ? 0 : toleranceM;

  const download = useCallback(async () => {
    if (!offsets) return;
    // NO pane size: the printed card is `STRIP_PNG_PANE`, not the reader's
    // window. Passing `box` here would make this button's file differ from the
    // Export menu's for the same run — the one thing spec §3.5 asks the
    // offscreen composition to prevent.
    await saveStripPng({ json, offsets, kind, toleranceM, spanM: renderSpanM, t });
  }, [offsets, json, kind, toleranceM, renderSpanM, t]);

  // The run never saved the arrays this picture is drawn from. Two lines and
  // no re-run button: the dock owns that affordance and offers it once, in
  // its header, for whichever pane needs it.
  const missing = (
    <div className='flex h-full flex-col items-start gap-1 overflow-auto py-4' data-testid='sv-strip-missing'>
      <p className='text-sm font-semibold'>{t('stationVolumes.strip.missingTitle')}</p>
      <p className='text-slate-500 dark:text-stone-400'>{t('stationVolumes.strip.missingBody')}</p>
    </div>
  );
  // Two byproducts, one question: did this run save the arrays this branch
  // draws from? A stack asks about `station_sections.f32`, everything else
  // about `station_offsets.f32`.
  const hasSource = isStack
    ? !!meta?.sections && !!meta.byproducts?.sections
    : !!meta?.offsetBins && !!meta.byproducts?.offsets;
  if (!hasSource) return missing;

  const q = isStack ? sectionsQ : offsetsQ;

  if (q.error) {
    return (
      <div className='flex h-full flex-col items-start gap-2 overflow-auto' data-testid='sv-strip-failed'>
        <p className='text-sm font-semibold'>{t('stationVolumes.strip.failed')}</p>
        <LoadingBadge
          name={json.alignment.name}
          phase='failed'
          error={q.error.message}
          labels={{ failed: t('stationVolumes.strip.failed') }}
        />
        <Button data-testid='sv-strip-retry' type='button' size='sm' variant='outline' onClick={() => q.refetch?.()}>
          {t('stationVolumes.errors.retry')}
        </Button>
      </div>
    );
  }

  if (!matrix) {
    // Not loading and not failed, yet nothing to draw: the query is DISABLED
    // — the run's offset axis is degenerate (`hasOffsetAxis` in
    // `useStationVolumes.ts`), or a stack chip's layer has no plane pair in
    // the file. Both are the same "no strip for this run" the missing-byproduct
    // branch reports, not a fetch that will ever finish.
    if (!q.isLoading) return missing;
    return (
      <LoadingBadge
        name={json.alignment.name}
        phase={q.progress?.phase ?? 'fetching'}
        progress={q.progress?.totalBytes ? q.progress.loadedBytes / q.progress.totalBytes : undefined}
        loadedBytes={q.progress?.loadedBytes}
        totalBytes={q.progress?.totalBytes ?? undefined}
        labels={{
          fetching: t('stationVolumes.errors.fetching'),
          decoding: t('stationVolumes.errors.decoding'),
          failed: t('stationVolumes.strip.failed'),
        }}
      />
    );
  }

  // The offset axis, from whichever file the picture came out of. Both carry
  // it, and both got it from `meta.params` — asking the matrix's own source
  // rather than re-deriving it keeps one answer.
  const axis = isStack ? sections : offsets;
  // The two elevations the tooltip quotes beside the value (spec §3.4). For a
  // stack they are the selected layer's two bounding levels, trimmed to the
  // rows the strip actually draws — the final station line is not one of them.
  const rows = matrix.nStations * matrix.nOffsets;
  const pair = isStack ? stack?.planePair : null;
  // The legend sentence, when the field is NOT `dz`. The default one names the
  // two surfaces the difference came from, which inverts on a thickness strip:
  // a THICKER layer is blue, and its as-built top is above design, not below.
  const stackLegend = !isStack
    ? undefined
    : bandOff
      ? t('stationVolumes.strip.stackLegendRaw')
      : effectiveToleranceM > 0
        ? t('stationVolumes.strip.stackLegend', { mm: Math.round(effectiveToleranceM * 1000) })
        : t('stationVolumes.strip.stackLegendNoTolerance');
  const zTop = pair ? sections?.planes[pair[0]]?.subarray(0, rows) : offsets?.zTop;
  const zBottom = pair ? sections?.planes[pair[1]]?.subarray(0, rows) : offsets?.zBottom;

  return (
    <div className='flex h-full min-h-0 flex-col gap-1'>
      <div className='flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-[11px]'>
        {/* The tolerance. A run measured against one shows THAT, read-only:
            the run's tolerance is the tolerance, and a box the reader could
            type into would suggest the verdict beside it moves with it.
            Neither control appears while the values are a raw thickness —
            there is no target for a band to sit around. */}
        {bandOff ? null : runTolM != null ? (
          <span className='sv-chip' data-testid='sv-strip-tol-chip'>
            {t('stationVolumes.strip.toleranceRun', { mm: Math.round(runTolM * 1000) })}
          </span>
        ) : (
          <label className='flex items-center gap-1' title={t('stationVolumes.strip.toleranceHelp')}>
            <span className='text-slate-500 dark:text-stone-400'>{t('stationVolumes.strip.tolerance')}</span>
            <NumericField
              data-testid='sv-strip-tol'
              className={cn(DOCK_CONTROL.size, 'w-16 px-1 py-0')}
              value={Math.round(toleranceM * 1000)}
              step='1'
              min='0'
              onChange={(mm) => setStoredTol(Math.max(0, mm) / 1000)}
            />
            <span className='text-slate-500 dark:text-stone-400'>mm</span>
          </label>
        )}

        <label className='flex items-center gap-1' title={t('stationVolumes.strip.spanHelp')}>
          <span className='text-slate-500 dark:text-stone-400'>{t('stationVolumes.strip.span')}</span>
          <NumericField
            data-testid='sv-strip-span'
            className={cn(DOCK_CONTROL.size, 'w-20 px-1 py-0')}
            value={Number(spanM.toFixed(3))}
            step='0.001'
            min='0'
            onChange={(m) => setStoredSpan(m)}
          />
          <span className='text-slate-500 dark:text-stone-400'>m</span>
        </label>

        {/* The same setting, dragged instead of typed. Logarithmic and centred
            on Auto (stripModel), because Δz runs over orders of magnitude
            between a wearing course and an earthworks run — on a linear track
            every useful span would sit in the first few pixels. The number
            field stays the way to enter a value the track cannot reach; a span
            outside it parks the thumb at an end without rewriting what is
            stored. */}
        <Slider
          data-testid='sv-strip-span-slider'
          className='w-56'
          aria-label={t('stationVolumes.strip.spanSlider')}
          title={t('stationVolumes.strip.spanSliderHelp')}
          min={0}
          max={SPAN_SLIDER_STEPS}
          step={1}
          value={[spanSliderPosition(spanM, autoSpanM)]}
          onValueChange={(v) => setStoredSpan(spanFromSliderPosition(v[0], autoSpanM))}
        />
        {/* Auto is a RESET, not a mode: it clears the override and the span
            goes back to following the data. Shown pressed while nothing is
            stored, so the reader can see which of the two they are on. */}
        {/* The dock kit's text button with the pressed look of the clearance
            cross-section's "Violations only" — one toggle style per dock, at
            the dock's one control height (`DOCK_CONTROL`), not `size='sm'`. */}
        <button
          data-testid='sv-strip-span-auto'
          type='button'
          className={cn(DOCK_CONTROL.button, DOCK_CONTROL.pressed)}
          aria-pressed={storedSpan == null}
          data-active={storedSpan == null ? 'true' : undefined}
          onClick={() => setStoredSpan(null)}
        >
          {t('stationVolumes.strip.spanAuto')}
        </button>

        {/* No PNG for a stack (ruling A-4): the byproduct writer needs a `dz`
            plane and a stack has none, so the button would hand back a picture
            of a different quantity than the one on screen. */}
        {isStack ? null : (
          <button
            data-testid='sv-strip-download'
            type='button'
            className={cn(DOCK_CONTROL.button, 'ml-auto')}
            onClick={() => void download()}
          >
            {t('stationVolumes.strip.downloadPng')}
          </button>
        )}
      </div>

      {/* What a stack strip IS, in the pane that draws it. Both halves are
          required: a reader who takes this for a Δz strip reads every colour
          wrong, and the line-per-interval approximation (spec §3.6) is stated
          rather than left to be discovered. */}
      {isStack && stackStrip ? (
        <p
          className='shrink-0 text-[11px] text-slate-500 dark:text-stone-400'
          data-testid='sv-strip-stack-note'
        >
          {`${stackStrip.designed ? t('stationVolumes.strip.stackThickness') : t('stationVolumes.strip.stackThicknessRaw')} ${t('stationVolumes.strip.stackLine')}`}
        </p>
      ) : null}

      <div ref={boxRef} className='min-h-0 flex-1'>
        <StripMapCanvas
          json={json}
          matrix={matrix}
          // All THREE planes: the tooltip quotes the two elevations the value
          // came from (spec §3.4), and it can only do that if they are passed.
          zTop={zTop}
          zBottom={zBottom}
          layerId={layerId}
          width={box.width}
          height={box.height}
          toleranceM={effectiveToleranceM}
          spanM={renderSpanM}
          offsetStartM={axis?.offsetStartM ?? 0}
          offsetStepM={axis?.offsetStepM ?? 0}
          kind={kind}
          swathLeftM={json.params.swathLeftM}
          swathRightM={json.params.swathRightM}
          referenceExtentLeftM={json.summary.referenceExtentLeftM ?? null}
          referenceExtentRightM={json.summary.referenceExtentRightM ?? null}
          legendText={stackLegend}
        />
      </div>
    </div>
  );
};
